import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  getDoc,
  setDoc,
  DocumentData
} from 'firebase/firestore';
import { db, auth } from './firebase';

export const audit = async (action: string, target: string) => {
  const user = auth.currentUser;
  if (!user) return;
  try {
    await addDoc(collection(db, "auditLogs"), {
      action,
      target,
      actor: user.email,
      actorUid: user.uid,
      createdAt: serverTimestamp()
    });
  } catch (e) {
    console.error("Audit log failed:", e);
  }
};

export const subscribeToCollection = <T extends DocumentData>(
  collectionName: string,
  callback: (data: T[]) => void,
  orderField: string = "createdAt",
  limitCount: number = 50
) => {
  let q = query(collection(db, collectionName), orderBy(orderField, "desc"), limit(limitCount));

  return onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as unknown as T));
    callback(data);
  }, (error) => {
    console.error(`Subscription error for ${collectionName}:`, error);
    if (error.code === 'failed-precondition' || error.message.includes('index')) {
      const fallbackQ = query(collection(db, collectionName), limit(limitCount));
      const unsubFallback = onSnapshot(fallbackQ, (snapshot) => {
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as unknown as T));
        callback(data);
      });
      return unsubFallback;
    }
  });
};

export const addData = async (collectionName: string, data: any) => {
  const docRef = await addDoc(collection(db, collectionName), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  await audit(`CREATE_${collectionName.toUpperCase()}`, docRef.id);
  return docRef;
};

export const updateData = async (collectionName: string, id: string, data: any) => {
  await updateDoc(doc(db, collectionName, id), {
    ...data,
    updatedAt: serverTimestamp()
  });
  await audit(`UPDATE_${collectionName.toUpperCase()}`, id);
};

export const upsertData = async (collectionName: string, id: string, data: any) => {
  await setDoc(doc(db, collectionName, id), {
    ...data,
    updatedAt: serverTimestamp()
  }, { merge: true });
  await audit(`UPSERT_${collectionName.toUpperCase()}`, id);
};

export const PROTECTED_COLLECTIONS = [
  'products',
  'templates',
  'portfolio',
  'services',
  'faq',
  'websiteRequests',
  'orders',
  'categories',
  'modifiers',
  'inventory',
  'invoices',
  'payments',
  'customers',
  'tables',
  'reservations',
  'waitlist',
  'promotions',
  'loyalty',
  'suppliers',
  'purchaseOrders',
  'branches',
  'employees',
  'settings'
];

export const deleteData = async (collectionName: string, id: string) => {
  if (PROTECTED_COLLECTIONS.includes(collectionName)) {
    console.error(`Attempted destructive deletion on protected collection: ${collectionName}`);
    throw new Error("Item is referenced by existing data and cannot be deleted. Archive it instead.");
  }
  await deleteDoc(doc(db, collectionName, id));
  await audit(`DELETE_${collectionName.toUpperCase()}`, id);
};

export const archiveData = async (collectionName: string, id: string) => {
  await updateDoc(doc(db, collectionName, id), {
    status: 'archived',
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  await audit(`ARCHIVE_${collectionName.toUpperCase()}`, id);
};

export async function trackSession(sessionId: string, data: any) {
  return setDoc(doc(db, "visitorSessions", sessionId), {
    ...data,
    lastActivity: serverTimestamp(),
    startedAt: serverTimestamp(),
    status: 'active'
  }, { merge: true });
}

export async function updateHeartbeat(sessionId: string) {
  return updateDoc(doc(db, "visitorSessions", sessionId), {
    lastActivity: serverTimestamp()
  });
}

export async function getSettings() {
  const docRef = doc(db, "settings", "global");
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : null;
}

export async function saveSettings(data: any) {
  await setDoc(doc(db, "settings", "global"), {
    ...data,
    updatedAt: serverTimestamp()
  }, { merge: true });
  await audit("UPDATE_SETTINGS", "global");
}

export async function isAdmin(uid: string) {
  const adminDoc = await getDoc(doc(db, "admins", uid));
  return adminDoc.exists() && adminDoc.data().status === "Active";
}
